import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, RefreshCw, Download, Phone, Mail, ArrowLeft, CalendarX } from 'lucide-react';
import { toast } from 'sonner';
import { PeriodSelector, fmtDate } from './audit/periodUtils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const isoDate = (d) => d.toISOString().slice(0, 10);

export default function DprComplianceReport() {
  const yst = new Date(); yst.setDate(yst.getDate() - 1);
  const [from, setFrom] = useState(isoDate(new Date(Date.now() - 6 * 86400000)));
  const [to, setTo] = useState(isoDate(yst));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [batchModal, setBatchModal] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/training/new-scorecard/dpr-gaps?date_from=${from}&date_to=${to}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load DPR gaps'))
      .finally(() => setLoading(false));
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = React.useCallback(() => {
    if (!data) return;
    const cols = ['trainer_name', 'trainer_email', 'trainer_phone', 'working_days', 'expected_sessions', 'missing_sessions', 'compliance_pct', 'missing_breakdown'];
    const lines = [cols.join(',')];
    data.rows.forEach(r => {
      const breakdown = r.batches.map(b => `#${b.batch_number}: ${b.missing_dates.map(x => `${x.date}(${x.day})`).join(';')}`).join(' | ');
      const row = [r.trainer_name, r.trainer_email || '', r.trainer_phone || '', (r.working_days || []).join('/'), r.expected_sessions, r.missing_sessions, r.compliance_pct, breakdown];
      lines.push(row.map(v => /[",\n]/.test(String(v ?? '')) ? `"${String(v).replace(/"/g, '""')}"` : String(v ?? '')).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `dpr-gaps-${from}_to_${to}.csv`;
    a.click();
  }, [data, from, to]);

  return (
    <div className="space-y-4" data-testid="dpr-compliance-page">
      <div className="flex items-start justify-between">
        <div>
          <Link to="/training/trainer-dashboard" className="text-[11px] text-violet-600 hover:underline inline-flex items-center gap-1 mb-1">
            <ArrowLeft className="w-3 h-3" />Trainer Dashboard
          </Link>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">DPR Compliance</h1>
          <p className="text-sm text-slate-400 mt-0.5">Trainers who haven&apos;t filled their daily attendance report (expected vs. submitted, holidays excluded)</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="h-8 text-[11px]" data-testid="dpr-refresh">
            <RefreshCw className="w-3 h-3 mr-1" />Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} className="h-8 text-[11px]" data-testid="dpr-export">
            <Download className="w-3 h-3 mr-1" />CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-3 flex flex-wrap items-end gap-3" data-testid="dpr-filters">
        <PeriodSelector defaultType="month" onChange={(range) => { setFrom(range.from_date); setTo(range.to_date); }} />
        {data && (
          <span className="ml-auto text-[10px] text-slate-500">
            Window <strong className="text-slate-700">{fmtDate(data.from)}</strong> → <strong className="text-slate-700">{fmtDate(data.to)}</strong>
          </span>
        )}
      </Card>

      {/* Summary KPI strip */}
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="dpr-kpis">
          <Card className="p-3 bg-slate-50 border-slate-200">
            <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-1">Total Trainers</p>
            <p className="text-2xl font-bold text-slate-700 tabular-nums">{data.summary.total_trainers}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">With expected sessions in window</p>
          </Card>
          <Card className="p-3 bg-rose-50 border-rose-200">
            <p className="text-[9px] uppercase tracking-wider text-rose-600 font-bold mb-1">Trainers With Gaps</p>
            <p className="text-2xl font-bold text-rose-700 tabular-nums">{data.summary.with_gaps}</p>
            <p className="text-[10px] text-rose-600 mt-0.5">Have at least 1 missing DPR</p>
          </Card>
          <Card className="p-3 bg-amber-50 border-amber-200">
            <p className="text-[9px] uppercase tracking-wider text-amber-600 font-bold mb-1">Missing Reports</p>
            <p className="text-2xl font-bold text-amber-700 tabular-nums">{data.summary.missing_sessions}</p>
            <p className="text-[10px] text-amber-600 mt-0.5">Of {data.summary.expected_sessions} expected</p>
          </Card>
          <Card className={`p-3 ${data.summary.compliance_pct >= 80 ? 'bg-emerald-50 border-emerald-200' : data.summary.compliance_pct >= 50 ? 'bg-amber-50 border-amber-200' : 'bg-rose-50 border-rose-200'}`}>
            <p className={`text-[9px] uppercase tracking-wider font-bold mb-1 ${data.summary.compliance_pct >= 80 ? 'text-emerald-600' : data.summary.compliance_pct >= 50 ? 'text-amber-600' : 'text-rose-600'}`}>Compliance</p>
            <p className={`text-2xl font-bold tabular-nums ${data.summary.compliance_pct >= 80 ? 'text-emerald-700' : data.summary.compliance_pct >= 50 ? 'text-amber-700' : 'text-rose-700'}`}>{data.summary.compliance_pct}%</p>
            <p className="text-[10px] mt-0.5 opacity-80">Sessions filled / expected</p>
          </Card>
        </div>
      )}

      {/* Trainer rows */}
      {loading ? (
        <p className="text-xs text-slate-500 italic py-6 text-center">Loading…</p>
      ) : data?.rows?.length === 0 ? (
        <Card className="p-6 text-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
          <p className="text-sm font-semibold text-emerald-700">All clear — every trainer has filled their DPR for this window</p>
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 380px)' }}>
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 shadow-sm z-10">
                <tr>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b w-8"></th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Trainer</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Working Days</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Expected</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Missing</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Compliance</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Contact</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map(r => {
                  const isExp = !!expanded[r.trainer_id];
                  const tone = r.compliance_pct >= 80 ? 'emerald' : r.compliance_pct >= 50 ? 'amber' : 'rose';
                  return (
                    <React.Fragment key={r.trainer_id || r.trainer_name}>
                      <tr className={`border-b border-slate-100 hover:bg-violet-50/30 cursor-pointer ${isExp ? 'bg-violet-50/40' : ''} ${r.missing_sessions === 0 ? 'opacity-60' : ''}`}
                          onClick={() => setExpanded(p => ({ ...p, [r.trainer_id]: !p[r.trainer_id] }))}
                          data-testid={`dpr-trainer-row-${r.trainer_id}`}>
                        <td className="px-3 py-1.5 text-center">{isExp ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}</td>
                        <td className="px-3 py-1.5 font-medium">{r.trainer_name}</td>
                        <td className="px-3 py-1.5 text-[10px] text-slate-500">{(r.working_days || []).join(' · ') || '—'}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{r.expected_sessions}</td>
                        <td className="px-3 py-1.5 text-right">
                          {r.missing_sessions > 0 ? (
                            <Badge variant="outline" className={`text-[10px] bg-${tone}-50 text-${tone}-700 border-${tone}-200`}>
                              <AlertTriangle className="w-2.5 h-2.5 mr-1" />{r.missing_sessions}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                              <CheckCircle2 className="w-2.5 h-2.5 mr-1" />0
                            </Badge>
                          )}
                        </td>
                        <td className={`px-3 py-1.5 text-right font-semibold tabular-nums text-${tone}-700`}>{r.compliance_pct}%</td>
                        <td className="px-3 py-1.5 text-[10px] text-slate-500" onClick={e => e.stopPropagation()}>
                          {r.trainer_phone && <a href={`tel:${r.trainer_phone}`} className="inline-flex items-center gap-1 hover:text-violet-600 mr-2"><Phone className="w-3 h-3" />{r.trainer_phone}</a>}
                          {r.trainer_email && <a href={`mailto:${r.trainer_email}`} className="inline-flex items-center gap-1 hover:text-violet-600"><Mail className="w-3 h-3" />Email</a>}
                        </td>
                      </tr>
                      {isExp && (
                        <tr>
                          <td colSpan={7} className="bg-slate-50/70 p-0">
                            <div className="px-6 py-3 border-l-2 border-violet-300">
                              {r.batches.length === 0 ? (
                                <p className="text-xs italic text-slate-400">No missing sessions — all DPRs filled.</p>
                              ) : (
                                <div className="space-y-2">
                                  {r.batches.map(b => (
                                    <button type="button" key={b.batch_id} onClick={() => setBatchModal({ ...b, trainer_name: r.trainer_name, working_days: r.working_days })}
                                      className="w-full text-left bg-white rounded border p-2 hover:border-violet-300 hover:bg-violet-50/40 transition" data-testid={`dpr-batch-card-${b.batch_id}`}>
                                      <div className="flex items-center justify-between mb-1">
                                        <p className="text-[11px] font-semibold text-slate-700">
                                          <span className="font-mono bg-violet-50 text-violet-700 px-1.5 py-0.5 rounded mr-1.5">#{b.batch_number}</span>
                                          {b.batch_name}
                                          {b.batch_status && <Badge variant="outline" className="text-[9px] ml-2">{b.batch_status}</Badge>}
                                        </p>
                                        <span className="text-[10px] text-slate-500 inline-flex items-center gap-1">{b.missing_dates.length} missing <ChevronRight className="w-3 h-3" /></span>
                                      </div>
                                      <div className="flex flex-wrap gap-1">
                                        {b.missing_dates.slice(0, 8).map(x => (
                                          <Badge key={x.date} variant="outline" className="text-[9px] bg-rose-50 text-rose-700 border-rose-200 font-mono">{x.date} <span className="ml-1 opacity-70">{x.day}</span></Badge>
                                        ))}
                                        {b.missing_dates.length > 8 && <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-500 border-slate-200">+{b.missing_dates.length - 8} more</Badge>}
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Per-batch detailed modal */}
      <Dialog open={!!batchModal} onOpenChange={(o) => !o && setBatchModal(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0 rounded-2xl" data-testid="dpr-batch-modal">
          {batchModal && (
            <>
              <DialogHeader className="px-6 py-4 border-b border-slate-100 bg-rose-50/50">
                <DialogTitle className="text-base flex items-center gap-2">
                  <CalendarX className="w-4 h-4 text-rose-600" />
                  <span className="font-mono bg-white border border-rose-200 text-rose-700 px-1.5 py-0.5 rounded text-[13px]">#{batchModal.batch_number}</span>
                  {batchModal.batch_name}
                </DialogTitle>
                <p className="text-[11px] text-slate-500 mt-1">
                  {batchModal.trainer_name} · {batchModal.batch_status || '—'} · working days: {(batchModal.working_days || []).join(', ') || '—'}
                  <span className="ml-2 font-semibold text-rose-600">{batchModal.missing_dates.length} DPR{batchModal.missing_dates.length > 1 ? 's' : ''} missing</span>
                </p>
              </DialogHeader>
              <div className="px-6 py-4 overflow-auto">
                <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-2">Days the trainer did not submit a DPR</p>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-[12px]">
                    <thead><tr className="bg-slate-50 text-left text-slate-500 border-b border-slate-200"><th className="py-1.5 px-3">Date</th><th className="py-1.5 px-3">Day</th><th className="py-1.5 px-3">Status</th></tr></thead>
                    <tbody>
                      {batchModal.missing_dates.map(x => (
                        <tr key={x.date} className="border-b border-slate-50">
                          <td className="py-2 px-3 font-mono text-slate-700">{fmtDate(x.date)}</td>
                          <td className="py-2 px-3"><Badge variant="outline" className={`text-[10px] ${['Sat', 'Sun'].includes(x.day) ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>{x.day}</Badge></td>
                          <td className="py-2 px-3"><span className="inline-flex items-center gap-1 text-rose-600 font-medium"><AlertTriangle className="w-3 h-3" />Not submitted</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[10px] text-slate-400 mt-2">Holidays and approved leave are already excluded via the trainer master calendar.</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
